/**
 * Analytics Tab Component
 * Approval analytics and metrics
 */
import React from 'react';
import { 
  TrendingUp, 
  Clock, 
  Users,
  BarChart3,
  AlertTriangle,
  CheckCircle2,
  XCircle
} from 'lucide-react';

export default function AnalyticsTab({ analytics, isLoading }) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-6 animate-pulse">
            <div className="h-6 bg-[#2F2F2F] rounded w-1/3 mb-4" />
            <div className="h-32 bg-[#2F2F2F] rounded" />
          </div>
        ))}
      </div>
    );
  }
  
  const velocityData = analytics?.velocityByLevel || [];
  const bottlenecks = analytics?.bottlenecks || [];
  const statusDist = analytics?.statusDistribution || {};
  const trend = analytics?.approvalTrend || [];
  
  return (
    <div className="space-y-6">
      {/* Velocity by Level */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[1, 2, 3].map(level => {
          const levelData = velocityData.find(v => v.level === level);
          return (
            <div key={level} className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs text-[#6B6B6B]">Level {level} Velocity</span>
                <Clock size={14} className="text-blue-400" />
              </div>
              <p className="text-2xl font-bold text-white">
                {levelData?.avgTimeHours?.toFixed(1) || '0'}<span className="text-sm text-[#6B6B6B] ml-1">hrs</span>
              </p>
              <p className="text-xs text-[#6B6B6B] mt-1">
                {levelData?.count || 0} approvals
              </p>
            </div>
          );
        })}
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Distribution */}
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-6">
          <h3 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
            <BarChart3 size={16} className="text-emerald-400" />
            Status Distribution
          </h3>
          
          <div className="space-y-3">
            {Object.entries(statusDist).map(([status, count]) => {
              const total = Object.values(statusDist).reduce((a, b) => a + b, 0);
              const percentage = total > 0 ? (count / total) * 100 : 0;
              
              const getConfig = (s) => {
                if (s.includes('approved')) return { color: 'bg-emerald-500', icon: CheckCircle2 };
                if (s.includes('rejected')) return { color: 'bg-red-500', icon: XCircle };
                return { color: 'bg-amber-500', icon: Clock };
              };
              
              const config = getConfig(status);
              const Icon = config.icon;
              
              return (
                <div key={status}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-[#A0A0A0] capitalize flex items-center gap-1">
                      <Icon size={10} />
                      {status.replace(/_/g, ' ')}
                    </span>
                    <span className="text-white">{count} ({percentage.toFixed(0)}%)</span>
                  </div>
                  <div className="h-2 bg-[#1F1F1F] rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${config.color} rounded-full transition-all`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
            
            {Object.keys(statusDist).length === 0 && (
              <p className="text-center text-sm text-[#6B6B6B] py-4">No data available</p>
            )}
          </div>
        </div>
        
        {/* Bottleneck Analysis */}
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-6">
          <h3 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
            <AlertTriangle size={16} className="text-amber-400" />
            Bottleneck Analysis
          </h3>
          
          {bottlenecks.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle2 size={32} className="mx-auto text-emerald-500/30 mb-2" />
              <p className="text-sm text-[#6B6B6B]">No bottlenecks detected</p>
            </div>
          ) : (
            <div className="space-y-3">
              {bottlenecks.slice(0, 5).map((approver, idx) => (
                <div 
                  key={idx} 
                  className="flex items-center justify-between p-2 rounded-lg bg-[#1A1A1A]"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${idx === 0 ? 'bg-red-500/20 text-red-400' : 'bg-[#2F2F2F] text-[#6B6B6B]'}`}>
                      {idx + 1}
                    </div>
                    <span className="text-sm text-white">{approver.approverName}</span>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm font-medium ${idx === 0 ? 'text-red-400' : 'text-white'}`}>
                      {approver.avgTimeHours?.toFixed(1) || 0}h
                    </p>
                    <p className="text-xs text-[#6B6B6B]">{approver.count} items</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Approval Trend */}
      <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-6">
        <h3 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
          <TrendingUp size={16} className="text-purple-400" />
          Approval Trend (Last 30 Days)
        </h3>
        
        {trend.length === 0 ? (
          <div className="text-center py-8">
            <BarChart3 size={32} className="mx-auto text-[#2F2F2F] mb-2" />
            <p className="text-sm text-[#6B6B6B]">No trend data available</p>
          </div>
        ) : (
          <div className="h-40 flex items-end gap-2">
            {trend.map((week, idx) => {
              const maxCount = Math.max(...trend.map(t => t.count), 1);
              const height = (week.count / maxCount) * 100;
              
              return (
                <div 
                  key={idx}
                  className="flex-1 flex flex-col items-center gap-1"
                >
                  <div 
                    className="w-full bg-gradient-to-t from-emerald-500 to-emerald-400 rounded-t transition-all hover:from-emerald-400 hover:to-emerald-300"
                    style={{ height: `${height}%`, minHeight: '4px' }}
                    title={`Week ${week.week}: ${week.count} approvals`}
                  />
                  <span className="text-[10px] text-[#6B6B6B]">W{week.week}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
